import { useState } from "react"
import { makeStyles } from "tss-react/mui"
import { useFormikContext } from "formik"
import { Grid, InputAdornment, Skeleton, TextField, Typography } from "@mui/material"
import {
    FetchDetailedPriceListsResponse,
    PriceListQuery,
    fetchDetailedPriceLists,
} from "../../../priceList/priceListApi"
import { toCurrency, toDineroLossy, toMonetaryAmount } from "../../../../../currency/currencyLib"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { CreateUpdateProductFormValues, PriceList } from "../../productsTypes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { CurrencyCode } from "../../../priceList/priceListTypes"
import { NumberFormatCustom } from "../FormikCurrencyField"

const useStylesSpecifyPrice = makeStyles()((theme) => ({
    title: {
        color: theme.palette.tailwind.slate[700],
    },
    titleBox: {
        paddingLeft: "2rem",
        marginTop: "1rem",
    },
    box: {
        width: "100%",
        boxShadow: "0px 2px 12px 0px #0000001F",
        padding: "2rem",
        borderRadius: "0.5rem",
    },
    input: {
        marginTop: "1.5rem",
    },
}))
type PriceListSpecifyPriceProps = Readonly<{
    id: number
    handleAdd: (el: PriceList) => void
}>
const selectPriceListDetails = (data: FetchDetailedPriceListsResponse) => data.priceList

export const PriceListSpecifyPrice = ({ id, handleAdd }: PriceListSpecifyPriceProps) => {
    const { values } = useFormikContext<CreateUpdateProductFormValues>()
    const [price, setPrice] = useState("")

    const { classes, cx } = useStylesSpecifyPrice()
    const detailedPriceList = useApiQuery(fetchDetailedPriceLists, {
        queryName: PriceListQuery.DETAILS,
        dependencies: {
            id,
        },
        select: selectPriceListDetails,
    })
    const handleAddClick = () => {
        if (detailedPriceList.data) {
            const { id, name } = detailedPriceList.data
            const priceList = {
                id: id,
                name: name,
                adjustedPrice: toMonetaryAmount(toDineroLossy(Number(price)!, toCurrency(CurrencyCode.DKK))),
            }
            handleAdd(priceList)
        }
    }

    if (detailedPriceList.data) {
        const { name, priceSetup } = detailedPriceList.data
        const percentage =
            (priceSetup?.vatCode?.vatPercentage.amount || 0) /
            Math.pow(10, priceSetup?.vatCode?.vatPercentage.scale || 0)
        return (
            <>
                <Grid container flexDirection="column" gap={5}>
                    <Typography variant="h4" fontWeight={200} className={cx(classes.title, classes.titleBox)}>
                        <LocalizedStrict id="product-create-price-list-selected-price-list">
                            <>Selected price list: </>
                        </LocalizedStrict>{" "}
                        <Typography variant="inherit" fontWeight={400} display="inline">
                            {name}
                        </Typography>
                    </Typography>

                    <Grid container flexDirection="column" alignItems="flex-start" gap={3} className={classes.box}>
                        <Grid container alignItems="center" justifyContent="space-between" gap={3}>
                            <Typography variant="h4" className={classes.title}>
                                {values.name ?? "-"}
                            </Typography>
                            <Typography variant="h4">
                                <Typography variant="inherit" fontWeight={200} display="inline">
                                    {values.currency ?? ""}
                                </Typography>{" "}
                                {values.price ?? ""}
                            </Typography>
                        </Grid>
                        <Typography variant="body2">
                            <LocalizedStrict id="product-create-price-list-table-vat-code">
                                <Typography variant="inherit" fontWeight={600} display="inline">
                                    VAT code:
                                </Typography>
                            </LocalizedStrict>
                            {""}
                            {priceSetup?.vatCode?.name} {`${percentage}%`}
                        </Typography>
                        <LocalizedStrict id="product-create-price-field-label" attrs={{ label: true }}>
                            <TextField
                                label="Price"
                                value={price}
                                inputProps={{
                                    style: { textAlign: "right" },
                                }}
                                onChange={(e) => {
                                    setPrice(e.target.value)
                                }}
                                InputProps={{
                                    inputComponent: NumberFormatCustom as any,
                                    startAdornment: (
                                        <InputAdornment position="start">{CurrencyCode.DKK}</InputAdornment>
                                    ),
                                }}
                                className={classes.input}
                            />
                        </LocalizedStrict>
                    </Grid>
                    <Grid container justifyContent="center">
                        <LocalizedStrict id="product-create-price-list-add-button">
                            <StandardButton fullWidth={false} onClick={handleAddClick} disabled={!price}>
                                Add
                            </StandardButton>
                        </LocalizedStrict>
                    </Grid>
                </Grid>
            </>
        )
    }
    return <Skeleton variant="rectangular" width="100%" height={400} />
}
